/**
 * The demo's lingering "do this next" cue (SKE-4234).
 *
 * Separate from the intro tour: once the tour has taught what the two sides
 * ARE, this cue tells the visitor what to DO — a soft spice "beacon" ring
 * breathes out of the Approve control on the client side, under an "Approve a
 * post ↓" label pill, then fades after ~6s.
 *
 * WHEN IT SHOWS
 * -------------
 * Only while the tour is NOT on screen and the visitor has never approved
 * anything (a dedicated `sked-demo-approved-once-v1` flag). On a first visit it
 * waits for the tour to close (the `TOUR_CLOSED_EVENT` from DemoTour); on a
 * later visit (tour already seen) it appears shortly after load. The moment the
 * visitor takes any approve / request action it clears and sets the flag, so it
 * never appears again once they've acted.
 *
 * PORTAL / STATIC
 * ---------------
 * Portals to `document.body` (a sibling of the approver `App`, never an
 * ancestor — nothing here re-anchors the approver's fixed confetti canvas), and
 * measures the Approve wrapper's rect rather than wrapping it. Under
 * `prefers-reduced-motion` the beacon's pulse is dropped to a faint static ring
 * (behind Tailwind's `motion-safe:`), while the ~6s auto-hide still runs.
 */

import { useEffect, useLayoutEffect, useState } from 'react'
import { createPortal } from 'react-dom'

import { SEEN_KEY, TOUR_CLOSED_EVENT } from './DemoTour'
import { demoStrings } from './demoStrings'
import { useDemoMirror } from './useDemoMirror'

/** Set once the visitor has ever approved/requested; suppresses the hint forever. */
const APPROVED_KEY = 'sked-demo-approved-once-v1'

/** How long the cue lingers before it fades out. */
const HINT_TTL_MS = 6000

/** On a later visit (tour already seen) let the board settle before pointing. */
const LATER_VISIT_DELAY_MS = 550

interface Rect {
  top: number
  left: number
  width: number
  height: number
}

const readFlag = (key: string): boolean => {
  try {
    return window.localStorage.getItem(key) === '1'
  } catch {
    return false
  }
}

const writeFlag = (key: string): void => {
  try {
    window.localStorage.setItem(key, '1')
  } catch {
    /* private mode — the hint simply behaves as if unseen next time */
  }
}

const readRect = (): Rect | null => {
  // The `[data-tour="approve"]` wrapper spans the whole footer (Approve +
  // Request); point at the actual Approve button inside it (the first button)
  // so the arrow lands on the green control, not the footer's centre.
  const wrapper = document.querySelector('[data-tour="approve"]')
  const el =
    wrapper?.querySelector('button') ??
    wrapper ??
    document.querySelector('[data-tour="client"]')
  if (!el) {
    return null
  }
  const r = el.getBoundingClientRect()
  return { top: r.top, left: r.left, width: r.width, height: r.height }
}

const DemoApproveHint: React.FC = () => {
  const { latestNotice } = useDemoMirror()

  const [approvedOnce, setApprovedOnce] = useState(() => readFlag(APPROVED_KEY))
  const [visible, setVisible] = useState(false)
  const [rect, setRect] = useState<Rect | null>(null)

  // Decide when to appear. First visit → wait for the tour to close; later
  // visit (tour already seen) → appear shortly after load. Never once approved.
  useEffect(() => {
    if (approvedOnce) {
      return
    }

    if (readFlag(SEEN_KEY)) {
      const id = window.setTimeout(() => setVisible(true), LATER_VISIT_DELAY_MS)
      return () => window.clearTimeout(id)
    }

    const onTourClosed = () => setVisible(true)
    document.addEventListener(TOUR_CLOSED_EVENT, onTourClosed)
    return () => document.removeEventListener(TOUR_CLOSED_EVENT, onTourClosed)
  }, [approvedOnce])

  // The first real approve / request action clears the cue and retires it.
  useEffect(() => {
    if (!latestNotice) {
      return
    }
    writeFlag(APPROVED_KEY)
    setApprovedOnce(true)
    setVisible(false)
  }, [latestNotice])

  // Auto-hide after a few seconds once shown.
  useEffect(() => {
    if (!visible) {
      return
    }
    const id = window.setTimeout(() => setVisible(false), HINT_TTL_MS)
    return () => window.clearTimeout(id)
  }, [visible])

  // Measure the Approve control while shown, and keep it correct on resize.
  useLayoutEffect(() => {
    if (!visible) {
      return
    }
    const measure = () => setRect(readRect())
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [visible])

  if (!visible) {
    return null
  }

  // If the target can't be found, anchor to the bottom-centre so the cue never
  // silently vanishes; otherwise sit just above the Approve control.
  const anchor = rect
    ? { left: rect.left + rect.width / 2, top: rect.top }
    : { left: window.innerWidth / 2, top: window.innerHeight - 24 }

  return createPortal(
    <>
      {/* The motion cue: a soft spice ring breathing OUT of the Approve button —
          replaces the old static arrow. Sits under the pill (z-54), over the
          button's exact rect, matched to its radius. Under `prefers-reduced-
          motion` the pulse is dropped and the faint static ring remains as a
          non-animated "here" marker. Only rendered once the target is measured. */}
      {rect && (
        <div
          aria-hidden="true"
          style={{
            left: rect.left,
            top: rect.top,
            width: rect.width,
            height: rect.height,
          }}
          className="pointer-events-none fixed z-[54] rounded-lg shadow-[0_0_0_3px_rgba(255,133,44,0.5)] motion-safe:animate-approve-beacon"
        />
      )}
      {/* The label pill, just above the Approve button. */}
      <div
        data-testid="demo-approve-hint"
        style={{
          left: anchor.left,
          top: anchor.top,
          transform: 'translate(-50%, calc(-100% - 8px))',
        }}
        className="pointer-events-none fixed z-[55] motion-safe:animate-in motion-safe:fade-in motion-safe:duration-300"
      >
        <span className="rounded-full border border-spice-200 bg-white px-3 py-1 font-gotham text-xs font-extrabold text-spice-700 shadow-lg">
          {demoStrings.approveHintLabel}
        </span>
      </div>
    </>,
    document.body
  )
}

export default DemoApproveHint
